import { Component, computed, inject, signal, TemplateRef, Type, viewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeWithoutTimezone, formatAsUTC, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc, ICellRendererParams } from 'ag-grid-community'
import { combineLatest, from, Observable, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { HeaderModal, TableHeaderComponent } from '../table/headers/table-header.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillCACPHourOverrideRow } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import openModal from 'src/app/shared/services/openModal'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { actionColumn } from '../table/cells/with-cell'
import { DateCellComponent, DateCellComponentProps } from '../table/cells/date-cell.component'
import { EntityCellComponent, EntityCellComponentProps } from '../table/cells/entity-cell.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { formatUser, getUserFilterConfig } from '../../shared/utilities/user'
import { FilterMeta } from '../../shared/services/filter/common'
import { TBillCACPHourOverrideModalComponent } from './tbill-ca-cp-hour-override-modal.component'

const fieldGroup = 'TBillCACPHourOverridesComponent'

export const hourOverrideHeaders: Readonly<Record<keyof TBillCACPHourOverrideRow, string>> = {
  control_area_code: 'Control Area',
  month: 'Month',
  hour: 'CP Hour Override (EST HB)',
  create_time: 'Created On (STL)',
  created_by: 'Created By',
  last_update_time: 'Last Updated (STL)',
  last_updated_by: 'Last Updated By',
}

const defaultColDef: ColDef = {
  sortable: true,
  filter: true,
  editable: false,
  width: 200,
  minWidth: 150,
  headerComponent: TableHeaderComponent,
  headerComponentParams: {
    filterGroup: fieldGroup,
    modalOptions: { centered: true },
    modalInit: {},
  },
}

const createColumn = (
  field: keyof TBillCACPHourOverrideRow,
  modal: Type<HeaderModal>,
  options: Partial<ColDef> = {},
): ColDef => ({
  headerName: hourOverrideHeaders[field],
  field,
  headerComponentParams: {
    ...defaultColDef.headerComponentParams,
    modal,
  },
  ...options,
})

const createControlAreaColumn = (): ColDef => {
  const cellRendererParams: EntityCellComponentProps<TBillCACPHourOverrideRow> = {
    value: row => row.control_area_code,
  }
  return createColumn('control_area_code', EntityFilterModalComponent, {
    flex: 1,
    cellRenderer: EntityCellComponent,
    cellRendererParams,
  })
}

const createDateColumn = (
  field: keyof TBillCACPHourOverrideRow,
  format: string,
  { viewAsTz, width }: { width?: number, viewAsTz?: string } = {},
): ColDef  => {
  const cellRendererParams: DateCellComponentProps<TBillCACPHourOverrideRow> = {
    date: row => row[field] as Date,
    format,
    ...viewAsTz ? { viewAsTz } : {},
  }
  return createColumn(field, DateRangeFilterModalComponent, {
    cellRenderer: DateCellComponent,
    cellRendererParams,
    ...width ? { width } : {},
  })
}

const createUserColumn = (field: keyof TBillCACPHourOverrideRow): ColDef =>
  createColumn(field, StringFilterModalComponent, {
    cellRenderer: ({ value } : ICellRendererParams<TBillCACPHourOverrideRow, string>) => formatUser(value),
  })

@Component({
  selector: 'app-tbill-ca-cp-hour-overrides',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    DateDisplayComponent,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="TBill Control Area Coincident Peak Overrides" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
      <app-active-filter-list [filterGroup]="constructor.name"></app-active-filter-list>
      <app-data-table
          heightStyle="calc(100vh - 320px)"
          [loading]="loading()"
          [data]="data()"
          [getRowId]="getRowId"
          [defaultColDef]="defaultColDef"
          [columnDefs]="columnDefs"
      />
      <app-table-pagination
          [pageNumber]="pageNumber()"
          [pageSize]="pageSize()"
          [totalResults]="totalResults()"
          (pageNumberChange)="pageNumber.set($event)"
          (pageSizeChange)="pageSize.set($event)"
      />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
      <div class='p-2'>
        <h3 class="mb-2">Are you sure you want to delete this CP Hour Override?</h3>
        @if(ctx) {
          <div app-labeled-property [label]="hourOverrideHeaders.control_area_code">
            {{ ctx.control_area_code }}
          </div>
          <div app-labeled-property [label]="hourOverrideHeaders.month">
            <div app-date-display [value]="ctx.month" [format]="monthOnlyFormat"></div>  
          </div>
          <div app-labeled-property [label]="hourOverrideHeaders.hour">                            
            <div app-date-display [value]="ctx.month" [format]="dateTimeWithoutTimezone"></div>  
          </div>
        }
      </div>
    </ng-template>
    `,
})
export class TBillCACPHourOverridesComponent {
  protected readonly monthOnlyFormat = monthOnlyFormat
  protected readonly dateTimeWithoutTimezone = dateTimeWithoutTimezone

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  private readonly deleteConfirmationTemplate =
    viewChild<TemplateRef<{ ctx: TBillCACPHourOverrideRow }>>('deleteConfirmation')

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillCACPHourOverrideRow[]>([])
  protected readonly paginationParams = computed((): PaginationParams => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly getRowId: GetRowIdFunc<TBillCACPHourOverrideRow> = ({ data: { control_area_code, month } }) =>
    `${control_area_code}${formatAsUTC(month)}`

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly defaultColDef: ColDef = defaultColDef

  protected readonly columnDefs: ColDef[] = [
    createControlAreaColumn(),
    createDateColumn('month', monthOnlyFormat),
    createDateColumn('hour', dateTimeWithoutTimezone, { width: 220 }),
    createDateColumn('create_time', dateTimeWithoutTimezone, { viewAsTz:  'America/Chicago' }),
    createUserColumn('created_by'),
    createDateColumn('last_update_time', dateTimeWithoutTimezone, { viewAsTz: 'America/Chicago' }),
    createUserColumn('last_updated_by'),
    actionColumn(fieldGroup, {
      actions: [
        {
          name: 'Update',
          onAction: (d: TBillCACPHourOverrideRow) => this.openUpdateModal(d),
        },
        {
          name: 'Delete',
          onAction: (d: TBillCACPHourOverrideRow) => this.openDeleteModal(d),
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService

    const filterMeta = (fieldName: keyof TBillCACPHourOverrideRow): FilterMeta => ({
      fieldGroup,
      fieldName,
      fieldDisplay: hourOverrideHeaders[fieldName],
    })

    dispatch(FilterService.InitFiltersAction({
      fieldGroup,
      fields: {
        month: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: true,
            meta: filterMeta('month'),
          },
        }, dispatch),
        control_area_code: new EntityFilter({
          config: {
            resource: 'controlAreas',
            allowMultiple: true,
            allowWildcards: false,
            meta: filterMeta('control_area_code'),
          },
        }, dispatch),
        hour: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            sendTime: true,
            onlyMonth: false,
            meta: filterMeta('hour'),
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: filterMeta('create_time'),
          },
        }, dispatch),
        created_by: new StringFilter(
          { config: getUserFilterConfig(fieldGroup, 'created_by', hourOverrideHeaders.created_by)  },
          dispatch,
        ),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: filterMeta('last_update_time'),
          },
        }, dispatch),
        last_updated_by: new StringFilter(
          { config: getUserFilterConfig(fieldGroup, 'last_updated_by', hourOverrideHeaders.last_updated_by) },
          dispatch,
        ),
      },
    }))

    const setData = (rows: TBillCACPHourOverrideRow[], total: number) =>  {
      this.totalResults.set(total)
      this.data.set(rows)
      this.loading.set(false)
    }

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(fieldGroup)
        .pipe(
          tap(() => {
            this.pageNumber.set(1)
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) =>
          this.tbillSettlementsService.getTBillCACPHourOverrides(pagination, filters, order),
        ),
      )
      .subscribe({
        error: e => {
          setData([], 0)
          this.toastService.showError(e)
        },
        next: ({ results, total }) => {
          setData(results, total)
        },
      })

    from(this.filterService.manageQueryParms(this.router, this.route, this.location, fieldGroup))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  private openCreateModal() {
    openModal(
      this.modalService,
      TBillCACPHourOverrideModalComponent,
      { centered: true },
    ).init({
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onSave: override => {
        this.saveChanges(
          this.tbillSettlementsService.createTBillCACPHourOverride(override),
          'Hour override created successfully',
        )
      },
    })
  }

  private openUpdateModal(row: TBillCACPHourOverrideRow) {
    openModal(
      this.modalService,
      TBillCACPHourOverrideModalComponent,
      { centered: true },
    ).init({
      row,
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onSave: ({ control_area_code, month, hour }) => {
        this.saveChanges(
          this.tbillSettlementsService.updateTBillCACPHourOverride(control_area_code, month, hour),
          'Updated successfully',
        )
      },
    })
  }

  private openDeleteModal(row: TBillCACPHourOverrideRow) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate(),
      bodyContext: row,
      onCancel: () => {
        this.modalService.dismissAll()
      },
      onConfirm: () => {
        this.saveChanges(
          this.tbillSettlementsService.deleteTBillCACPHourOverride(row.control_area_code, row.month),
          'Hour override deleted successfully',
        )
      },
    })
  }

  private saveChanges(httpOp: Observable<unknown>, successMessage: string) {
    httpOp.subscribe({
      next: ()=> {
        this.toastService.show({ type: 'success', message: successMessage })
        this.modalService.dismissAll()
        this.refresh.update(_ => _ + 1)
      },
      error: e => {
        this.toastService.showError(e)
        this.modalService.dismissAll()
      },
    })
  }

  protected readonly hourOverrideHeaders = hourOverrideHeaders
}